// Services.jsx
import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  FaCode,
  FaBrain,
  FaMobile,
  FaDesktop,
  FaPalette,
  FaChartLine,
  FaCheckCircle,
  FaUsers,
  FaArrowRight,
  FaRocket,
  FaCog,
  FaLightbulb,
} from "react-icons/fa";

const Services = () => {
  // Full-Screen Hero Section
  const HeroSection = () => (
    <section className="relative h-screen w-full overflow-hidden">
      {/* Full Screen Background Image */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&q=80"
          alt="Our Services"
          className="w-full h-full object-cover grayscale"
        />
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/60 to-black/80"></div>
      </div>

      {/* Animated Blobs */}
      <div className="absolute inset-0 overflow-hidden z-10 pointer-events-none">
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.2, 0.4, 0.2],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-500/30 rounded-full blur-[120px]"
        />
        <motion.div
          animate={{
            scale: [1, 1.3, 1],
            opacity: [0.1, 0.3, 0.1],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/30 rounded-full blur-[120px]"
        />
      </div>

      {/* Hero Content - Centered */}
      <div className="relative z-20 flex h-full items-center justify-center px-4">
        <div className="max-w-5xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: "easeOut" }}
          >
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.8 }}
              className="mb-4 text-sm font-semibold uppercase tracking-widest text-cyan-400"
            >
              What We Offer
            </motion.p>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.8 }}
              className="mb-6 text-5xl font-extrabold tracking-tighter text-white md:text-7xl lg:text-8xl drop-shadow-2xl"
            >
              Innovative
              <br />
              <span className="bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                Digital Solutions
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.8 }}
              className="mx-auto mb-10 max-w-3xl text-lg font-light leading-relaxed text-gray-200 md:text-xl"
            >
              Comprehensive digital solutions engineered for growth. From
              concept to deployment, we deliver excellence in every project.
            </motion.p>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-30">
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="flex flex-col items-center gap-2 text-white/70"
        >
          <span className="text-xs uppercase tracking-widest">Scroll</span>
          <div className="h-12 w-[2px] bg-gradient-to-b from-white to-transparent"></div>
        </motion.div>
      </div>
    </section>
  );

  // Services Data
  const services = [
    {
      id: "software-development",
      icon: FaCode,
      title: "Software Development",
      subtitle: "Web, Mobile, Desktop",
      description:
        "Build powerful web applications, mobile apps, and desktop software tailored to your business needs. We leverage modern technologies to create scalable, performant solutions.",
      features: [
        "Custom Web Applications",
        "Mobile App Development (iOS & Android)",
        "Desktop Application Development",
        "Progressive Web Apps (PWA)",
      ],
      gradient: "from-blue-500 to-cyan-500",
    },
    {
      id: "ai-ml",
      icon: FaBrain,
      title: "AI & Machine Learning",
      subtitle: "Automation, Insights",
      description:
        "Harness the power of artificial intelligence and machine learning to automate processes, gain insights, and make data-driven decisions.",
      features: [
        "Machine Learning Models",
        "Natural Language Processing",
        "Computer Vision Solutions",
        "Predictive Analytics",
      ],
      gradient: "from-purple-500 to-pink-500",
    },
    {
      id: "ui-ux-design",
      icon: FaPalette,
      title: "UI/UX Design",
      subtitle: "User Interface, Brand Identity",
      description:
        "Create stunning, user-friendly interfaces that delight your customers. Our design team crafts experiences that drive engagement and conversion.",
      features: [
        "User Interface Design",
        "User Experience Research",
        "Wireframing & Prototyping",
        "Brand Identity Design",
      ],
      gradient: "from-pink-500 to-rose-500",
    },
    {
      id: "mobile-development",
      icon: FaMobile,
      title: "Mobile Development",
      subtitle: "iOS, Android, Cross-platform",
      description:
        "Native and cross-platform mobile applications that deliver exceptional performance and user experience across all devices.",
      features: [
        "Native iOS Development",
        "Native Android Development",
        "React Native Apps",
        "Flutter Development",
      ],
      gradient: "from-cyan-500 to-teal-500",
    },
    {
      id: "erp",
      icon: FaDesktop,
      title: "Enterprise ERP",
      subtitle: "Enterprise, Cloud",
      description:
        "Streamline your business operations with comprehensive ERP solutions. Integrate all aspects of your business into one unified system.",
      features: [
        "Custom ERP Development",
        "Cloud-Based ERP",
        "ERP Integration",
        "Business Process Automation",
      ],
      gradient: "from-indigo-500 to-purple-500",
    },
    {
      id: "digital-marketing",
      icon: FaChartLine,
      title: "Digital Marketing",
      subtitle: "SEO, Growth",
      description:
        "Grow your online presence with data-driven digital marketing strategies. From SEO to social media, we help you reach your target audience.",
      features: [
        "Search Engine Optimization",
        "Content Marketing",
        "Social Media Marketing",
        "Pay-Per-Click Advertising",
      ],
      gradient: "from-orange-500 to-red-500",
    },
    {
      id: "testing",
      icon: FaCheckCircle,
      title: "End to End Testing",
      subtitle: "QA, Security",
      description:
        "Ensure quality and security with comprehensive testing services. We identify and fix issues before they reach your users.",
      features: [
        "Automated Testing",
        "Manual Testing",
        "Performance Testing",
        "Security Testing",
      ],
      gradient: "from-green-500 to-emerald-500",
    },
    {
      id: "staffing",
      icon: FaUsers,
      title: "Contract Staffing",
      subtitle: "Expert Resources",
      description:
        "Access skilled professionals on-demand. We provide talented developers, designers, and specialists to augment your team.",
      features: [
        "Full-Stack Developers",
        "Frontend Specialists",
        "Backend Engineers",
        "DevOps Engineers",
      ],
      gradient: "from-yellow-500 to-orange-500",
    },
  ];

  // Service Cards Section
  const ServiceCards = () => (
    <section className="relative bg-[#f8fafc] py-24 px-4 overflow-hidden">
      <div className="absolute top-0 right-0 -translate-y-1/4 translate-x-1/4 w-[500px] h-[500px] bg-cyan-500/5 rounded-full blur-[100px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 translate-y-1/4 -translate-x-1/4 w-[500px] h-[500px] bg-purple-500/5 rounded-full blur-[100px] pointer-events-none" />

      <div className="relative mx-auto max-w-7xl z-10">
        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-2">
          {services.map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ delay: index * 0.05, duration: 0.5 }}
              whileHover={{ y: -5 }}
              className="group relative overflow-hidden rounded-3xl border border-white/60 bg-white/60 p-8 shadow-xl backdrop-blur-md transition-all hover:shadow-2xl hover:bg-white/80"
            >
              <div
                className={`absolute inset-0 bg-gradient-to-br ${service.gradient} opacity-0 transition-opacity duration-500 group-hover:opacity-[0.08]`}
              />

              <div className="relative">
                <div
                  className={`mb-6 inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br ${service.gradient} text-3xl text-white shadow-md transition-transform group-hover:scale-105`}
                >
                  <service.icon />
                </div>

                <h3 className="mb-2 text-2xl font-bold text-gray-900">
                  {service.title}
                </h3>
                <p className="mb-4 text-sm font-medium text-gray-500">
                  {service.subtitle}
                </p>
                <p className="mb-8 text-gray-600 leading-relaxed">
                  {service.description}
                </p>

                <ul className="space-y-3 mb-8 border-t border-gray-100 pt-6">
                  {service.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <FaCheckCircle
                        className={`mt-1 flex-shrink-0 text-sm bg-gradient-to-br ${service.gradient} bg-clip-text text-transparent opacity-80`}
                      />
                      <span className="text-sm text-gray-700 font-medium">
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>

                <Link to={`/services/${service.id}`}>
                  <motion.div
                    whileHover={{ x: 5 }}
                    className={`flex items-center gap-2 font-bold text-sm uppercase tracking-wider bg-gradient-to-r ${service.gradient} bg-clip-text text-transparent group-hover:gap-3 transition-all cursor-pointer w-fit`}
                  >
                    Learn More
                    <FaArrowRight className="text-sm" />
                  </motion.div>
                </Link>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );

  // Why Choose Us Section
  const WhyChooseUs = () => {
    const reasons = [
      {
        icon: FaRocket,
        title: "Fast Delivery",
        description: "Quick turnaround without compromising quality",
      },
      {
        icon: FaCog,
        title: "Cutting-Edge Tech",
        description: "Latest technologies and best practices",
      },
      {
        icon: FaLightbulb,
        title: "Innovative Solutions",
        description: "Creative approaches to complex problems",
      },
      {
        icon: FaUsers,
        title: "Expert Team",
        description: "Skilled professionals dedicated to your success",
      },
    ];

    return (
      <section className="relative bg-black py-24 px-4 text-white overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-gray-900 to-black opacity-50"></div>

        <div className="relative mx-auto max-w-7xl z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16 text-center"
          >
            <h2 className="mb-4 text-4xl font-bold md:text-5xl">
              Why Choose <span className="text-cyan-400">Nexonetics?</span>
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              We blend technical expertise with creative innovation to deliver
              excellence in every project.
            </p>
          </motion.div>

          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {reasons.map((reason, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className="rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur-sm transition-all hover:border-cyan-500/30 hover:bg-white/10"
              >
                <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-cyan-500/10 text-2xl text-cyan-400">
                  <reason.icon />
                </div>
                <h3 className="mb-2 text-xl font-bold">{reason.title}</h3>
                <p className="text-sm text-gray-400 leading-relaxed">
                  {reason.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  // CTA Section
  const CTASection = () => (
    <section className="bg-gradient-to-r from-slate-900 via-purple-900 to-slate-900 py-24 px-4 relative overflow-hidden">
      <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>

      <div className="relative mx-auto max-w-4xl text-center text-white z-10">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-6 text-4xl font-extrabold md:text-5xl tracking-tight"
        >
          Ready to Start Your Project?
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="mb-10 text-lg text-blue-100/80 max-w-2xl mx-auto"
        >
          Let's transform your vision into reality. Get in touch with our team
          today for a consultation.
        </motion.p>
        <Link to="/contact">
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="group inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 text-lg font-bold text-purple-900 shadow-[0_0_20px_rgba(255,255,255,0.3)] transition-all hover:shadow-[0_0_30px_rgba(255,255,255,0.5)]"
          >
            Request a Quote
            <FaArrowRight className="transition-transform group-hover:translate-x-1" />
          </motion.button>
        </Link>
      </div>
    </section>
  );

  return (
    <div className="bg-white overflow-x-hidden">
      <HeroSection />
      <ServiceCards />
      <WhyChooseUs />
      <CTASection />
    </div>
  );
};

export default Services;
